import React from "react";
import {
  Tooltip,
  type TooltipProps,
  List,
  ListItem,
  Box,
  Divider,
} from "@mui/material";
import { Typography } from "@platform/design-system";

import { BROKER_DOCUMENT_TYPE_LABELS } from "../constants";
import { documentTypeToLabel, isAnalystDocument } from "../utils";

import type { DocumentType } from "../types";

type DocumentDetailsTooltipProps = {
  pages: number;
  summary?: string[] | undefined;
  brokerDocTypes?: string[];
  aiDocType?: DocumentType;
  url: string;
  plans: string[];
  children: React.ReactElement;
};

export const DocumentDetailsTooltip = ({
  children,
  pages,
  summary,
  brokerDocTypes,
  aiDocType,
  url,
  plans,
}: DocumentDetailsTooltipProps): React.JSX.Element => {
  const isAnalystSource = isAnalystDocument(url);
  const items = [
    `${pages} ${pages === 1 ? "page" : "pages"}`,
    ...(summary ?? []),
    `Plans: ${plans.length ? plans.join(", ") : "–"}`,
  ];

  const tooltipContent = (
    <>
      <Typography variant="body-medium" display="block" mb={0.5}>
        <strong>Document Details</strong>
      </Typography>
      <List dense disablePadding>
        {items.map((item) => (
          <ListItem key={item} disablePadding sx={{ py: 0.25, px: 1 }}>
            <Typography variant="body-medium" color="textSecondary">
              • {item}
            </Typography>
          </ListItem>
        ))}
      </List>

      {(brokerDocTypes?.length || aiDocType) && (
        <>
          <Divider sx={{ my: 2 }} />
          {brokerDocTypes?.length && (
            <Typography variant="body-medium" mb={0.5}>
              Broker Label:{" "}
              <Typography component="span" variant="body-medium">
                <strong>
                  {brokerDocTypes
                    .map((type) => {
                      const normalized = type.trim().toLowerCase();
                      return (
                        BROKER_DOCUMENT_TYPE_LABELS[normalized] ?? type.trim()
                      );
                    })
                    .join(", ")}
                </strong>
              </Typography>
            </Typography>
          )}
          {aiDocType && (
            <Typography variant="body-medium">
              {isAnalystSource ? "Manual Doc. Label" : "AI Doc. Type"}:{" "}
              <Typography component="span" variant="body-medium">
                <strong>{documentTypeToLabel(aiDocType)}</strong>
              </Typography>
            </Typography>
          )}
        </>
      )}
    </>
  );

  const slotProps: TooltipProps["slotProps"] = {
    tooltip: {
      sx: {
        bgcolor: "background.paper",
        color: "text.primary",
        border: "1px solid",
        borderColor: "divider",
        p: 1.5,
        minWidth: 140,
        borderRadius: 1.5,
      },
    },
    arrow: {
      sx: { color: "background.paper" },
    },
  };
  return (
    <Tooltip
      title={tooltipContent}
      placement="bottom-start"
      slotProps={slotProps}
    >
      <Box sx={{ cursor: "pointer", display: "inline-flex" }}>{children}</Box>
    </Tooltip>
  );
};
